/**
 * attachmentEntry — union-shape discriminator for owner.documents[] /
 * owner.attachments[] entries.
 *
 * Cited: `Specifications/FILES_SCOPE_AND_VISIBILITY.md` §4b (canonical shape) +
 *        operator ruling 2026-08-03 (declare the discriminator, don't infer it).
 *
 * ─── The union ──────────────────────────────────────────────────────────────
 *
 * Owner-side attachment arrays historically held **legacy inline documents**
 * and now also hold **AttachmentRefs** (PT-53b, DEC-60). Both shapes exist
 * in the same array on the same record — a single `documents[]` on a job
 * can contain either shape, or both, and readers must handle both.
 *
 * **Shape A — LEGACY inline document** *(customer.documents[], job.documents[],
 *   settings-list-`documents`, invoices legacy, quote legacy)*:
 *
 *   {
 *     id:           string,                 // opaque legacy id
 *     name:         string,                 // filename
 *     type?:        string,                 // MIME
 *     size?:        number,                 // bytes
 *     data_url?:    string,                 // base64 payload (small files)
 *     url?:         string,                 // presigned GET or public URL
 *     uploaded_at?: string,                 // ISO date
 *   }
 *
 * **Shape B — AttachmentRef** *(new, produced by AttachModal onAttached)*:
 *
 *   {
 *     attachmentId: string,                 // B2 Attachment record id (or library id)
 *     source:       'own' | 'library',      // R-LIB-*
 *     filename:     string,
 *     contentType:  string,                 // MIME
 *     sizeBytes:    number,
 *     attachedAt:   string,                 // ISO date
 *   }
 *
 * ─── The discriminator ──────────────────────────────────────────────────────
 *
 * **Presence of `attachmentId` is the discriminator.** No AttachmentRef exists
 * without one; no legacy inline entry writes one. Any reader that must
 * differentiate the shapes calls `isAttachmentRef(entry)` — never sniffs
 * `filename` vs `name` directly and never reads `entry.type` without a
 * pre-normalization pass.
 *
 * ─── Why not merge the shapes? ──────────────────────────────────────────────
 *
 * A rename `documents[] → attachments[]` and a shape unification would be
 * a migration touching 10 consuming files + all existing tenant data. That
 * work is **filed as post-beta debt** (see tech-debt.md entry "Two legacy
 * inline-data-URL upload flows bypass B2 backend"). Until then: two shapes
 * live in one array, and this module makes the branching deliberate.
 *
 * ─── Readers today (2026-08-03) ─────────────────────────────────────────────
 *
 * Adopting this module: **JobDetail FilesTab render · WorkOrderDetail
 * Attachments card render.**
 *
 * Deferred to legacy migration (still branching implicitly on `name`↔
 * `filename`, `url`↔`download_url`, `size`↔`sizeBytes` fallbacks):
 *   • CustomerDetail DocumentsTab (writer + reader)
 *   • settings/DocumentsPage (writer + reader — the Files repo itself)
 *   • InvoiceReviewSend (2 sites)
 *   • QuoteDocument printable list
 *   • QuoteDetail attachments block
 *   • ChangeOrderBuilder attachments list
 *   • WorkOrderDetail startEdit `wo.attachments.map(a => typeof a === 'string'
 *     ? { name: a } : { ...a })` — the highest-risk coercion site;
 *     AttachmentRef survives via `{...a}` spread only because `name` is
 *     never mandatory-read from it downstream. When migration lands, this
 *     coercion should be replaced with `normalizeEntry(a)` and the
 *     inline-string branch dropped.
 */

// ────────────────────────────────────────────────────────────────────────────
// Discriminator
// ────────────────────────────────────────────────────────────────────────────

/**
 * `true` iff the entry is an AttachmentRef (Shape B) — i.e. produced by
 * AttachModal.onAttached or hydrated from a record whose attachments[] was
 * written after PT-53b. Detects on `attachmentId` presence, not by absence
 * of legacy fields, so a hybrid entry that carries both wins as Shape B.
 */
export function isAttachmentRef(entry) {
  return !!(entry && typeof entry === 'object' && typeof entry.attachmentId === 'string' && entry.attachmentId.length > 0);
}

/**
 * `true` iff the entry is a legacy inline document (Shape A). Not simply
 * `!isAttachmentRef` — an entry with neither an attachmentId nor an id is
 * malformed and returns `false` on both predicates so callers can render
 * a fallback rather than a corrupt row.
 */
export function isLegacyDocument(entry) {
  if (!entry || typeof entry !== 'object') return false;
  if (isAttachmentRef(entry)) return false;
  // Legacy entries always carry an id + a name (data_url and uploaded_at
  // are optional); the discriminator here is "not a Ref, has an id".
  return typeof entry.id === 'string' && entry.id.length > 0;
}

// ────────────────────────────────────────────────────────────────────────────
// Normalizer
// ────────────────────────────────────────────────────────────────────────────

/**
 * Project either shape into a common view-shape for rendering. Readers that
 * only need to *display* an entry (icon + name + size + date + open action)
 * should call `entryDisplay(entry)` rather than branching on the union
 * themselves — that keeps the branching in one place.
 *
 * Returns:
 *   {
 *     key:         string,                  // stable for React key
 *     shape:       'attachment' | 'legacy' | 'unknown',
 *     name:        string,                  // display filename
 *     mimeType:    string,                  // MIME or '' if unknown
 *     bytes:       number,                  // 0 if unknown
 *     uploadedAt:  string,                  // ISO or ''
 *     openHref:    string | null,           // direct-open URL if any
 *     source:      'own' | 'library' | null,// only set for AttachmentRef
 *     raw:         object,                  // pass-through for callers that need
 *                                             deeper access (delete flow, retry, etc.)
 *   }
 *
 * Rendering rules the normalizer enforces:
 *   • Name: `filename` (Ref) or `name` (Legacy) — never both.
 *   • Bytes: `sizeBytes` (Ref) or `size` (Legacy).
 *   • Date: `attachedAt` (Ref) or `uploaded_at` (Legacy).
 *   • Href: `url` (Legacy inline) — Refs today have no direct-open URL until
 *     a `getAttachmentDownloadUrl` call resolves; that's a post-beta wire.
 */
export function entryDisplay(entry) {
  if (isAttachmentRef(entry)) {
    return {
      key: entry.attachmentId,
      shape: 'attachment',
      name: entry.filename || 'Untitled',
      mimeType: entry.contentType || '',
      bytes: Number(entry.sizeBytes) || 0,
      uploadedAt: entry.attachedAt || '',
      openHref: null, // presign-on-click; UI wires this in commit 2+
      source: entry.source === 'library' ? 'library' : 'own',
      raw: entry,
    };
  }
  if (isLegacyDocument(entry)) {
    return {
      key: entry.id,
      shape: 'legacy',
      name: entry.name || entry.fileName || 'Untitled',
      mimeType: entry.type || '',
      bytes: Number(entry.size) || 0,
      uploadedAt: entry.uploaded_at || entry.uploadedAt || '',
      openHref: entry.url || entry.data_url || null,
      source: null, // legacy predates R-LIB-*
      raw: entry,
    };
  }
  return {
    key: (entry && (entry.id || entry.attachmentId)) || 'unknown',
    shape: 'unknown',
    name: 'Corrupt attachment entry',
    mimeType: '',
    bytes: 0,
    uploadedAt: '',
    openHref: null,
    source: null,
    raw: entry,
  };
}

/**
 * Format bytes for display. Small helper so consumers don't each define
 * their own — the exact rounding matches AttachModal.humanBytes.
 */
export function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Canonical identity key for an attachment/document entry. Used for deduplication
 * when merging `documents[]` (canonical) and `attachments[]` (legacy) arrays.
 * IDI-289 (2026-09-09): hoisted from four inline expressions that used different
 * fallback chains. The stored entry always carries `attachmentId` (Ref shape) or
 * `id` (legacy shape); `attachment_id` (snake) is a request-body field name only
 * (api.js:376,379,382) and never appears on a stored entry.
 */
export function attachmentIdOf(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return entry.attachmentId || entry.id || null;
}

/**
 * Merge and deduplicate `documents[]` + `attachments[]` from a quote-like record.
 * Returns a single array with duplicates removed by `attachmentIdOf`.
 *
 * Order contract (IDI-289, 2026-09-09): `documents[]` (canonical) comes FIRST,
 * `attachments[]` (legacy) second. When a doc appears in both arrays, the
 * canonical copy survives. This is deliberate — canonical-first means the
 * display order and the surviving shape both favour the newer write path.
 *
 * Identity contract: entries with NO identity (`attachmentIdOf` returns null)
 * are ALWAYS kept — never deduped. Two id-less entries with identical content
 * both survive. This preserves the load-bearing property documented at
 * SentQuoteRightPanel.js:188-199 (§353 §3.1): "two entries with no identifying
 * data render as TWO rows (not one)."
 *
 * IDI-289 (2026-09-09): hoisted so QuoteBuilderPage, SentQuoteRightPanel, and
 * any future reader share one implementation instead of copying the merge+dedupe.
 */
export function mergeDocumentEntries(record) {
  if (!record || typeof record !== 'object') return [];
  const docs = Array.isArray(record.documents) ? record.documents : [];
  const legacy = Array.isArray(record.attachments) ? record.attachments : [];
  const seen = new Set();
  return [...docs, ...legacy].filter((entry) => {
    const k = attachmentIdOf(entry);
    if (k === null) return true; // no identity → always keep; caller disambiguates
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * Remove a document/attachment entry by its canonical ID.
 * IDI-289 (2026-09-09): exported so the production call site and the
 * behavioural test import the SAME function. A test that re-types the
 * expression can't catch a regression in the production line.
 */
export function removeDocumentById(entries, attId) {
  return entries.filter((e) => attachmentIdOf(e) !== attId);
}
